import React from 'react';

const shimmer = 'animate-pulse bg-muted rounded';

// Single skeleton block
const SkeletonBlock = ({ className = '' }) => (
  <div className={`${shimmer} ${className}`} />
);

// Card skeleton — for grid/card layouts
const CardSkeleton = () => (
  <div className="bg-card border border-border rounded-lg p-5 space-y-3">
    <div className="flex items-center gap-3">
      <SkeletonBlock className="w-10 h-10 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonBlock className="h-4 w-3/4" />
        <SkeletonBlock className="h-3 w-1/2" />
      </div>
    </div>
    <SkeletonBlock className="h-3 w-full" />
    <SkeletonBlock className="h-3 w-5/6" />
    <div className="flex items-center justify-between pt-1">
      <SkeletonBlock className="h-5 w-16 rounded-full" />
      <SkeletonBlock className="h-8 w-20 rounded-md" />
    </div>
  </div>
);

// List-row skeleton — for list/feed layouts
const ListRowSkeleton = () => (
  <div className="flex items-center gap-3 py-3 px-4">
    <SkeletonBlock className="w-9 h-9 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <SkeletonBlock className="h-4 w-2/3" />
      <SkeletonBlock className="h-3 w-1/3" />
    </div>
    <SkeletonBlock className="h-5 w-16 rounded-full flex-shrink-0" />
  </div>
);

// Table-row skeleton — for table layouts
const TableRowSkeleton = ({ cols = 5 }) => (
  <tr>
    {Array.from({ length: cols })?.map((_, i) => (
      <td key={i} className="px-4 py-3">
        <SkeletonBlock className={`h-4 ${i === 0 ? 'w-3/4' : i === cols - 1 ? 'w-16' : 'w-1/2'}`} />
      </td>
    ))}
  </tr>
);

/**
 * Skeleton — shared animated placeholder for loading states.
 *
 * Props:
 *   variant: 'card' | 'list-row' | 'table-row'  (default: 'card')
 *   count:   number of skeleton items to render   (default: 3)
 *   cols:    number of columns for table-row variant (default: 5)
 */
const Skeleton = ({ variant = 'card', count = 3, cols = 5 }) => {
  const items = Array.from({ length: count });

  if (variant === 'list-row') {
    return (
      <div className="divide-y divide-border">
        {items?.map((_, i) => <ListRowSkeleton key={i} />)}
      </div>
    );
  }

  if (variant === 'table-row') {
    return (
      <>
        {items?.map((_, i) => <TableRowSkeleton key={i} cols={cols} />)}
      </>
    );
  }

  // default: card
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items?.map((_, i) => <CardSkeleton key={i} />)}
    </div>
  );
};

export { CardSkeleton, ListRowSkeleton, TableRowSkeleton };
export default Skeleton;
